import {
  getDiagnosisFromCache,
  putDiagnosisInCache,
  pruneOldDiagnosisCacheOncePerDay,
} from 'src/clinic/components/ClinicManageBookingsPage/consultsTab/diagnosis/apptDiagnosisCache'
import {
  fetchDoctorLabelsForEvents,
  fetchTherapistLabelsForEvents,
} from 'src/clinic/components/ClinicManageBookingsPage/consultsTab/diagnosis/apptDiagnosisService'
import { EVENT_TYPE_THERAPY_SESSION } from 'src/consumer/constants'

/**
 * Day-level label loader: primes the IndexedDB cache for a whole day's appointment events in ONE
 * bulk network call per route, then notifies mounted chips to re-read the cache.
 *
 * Route depends on event type: consultations → doctor Diagnosis/Complaint label; therapy sessions
 * → therapy-type label. Each route is fetched in a single batched request.
 *
 * Design for performance / weak machines:
 *  - One request per route per batch of uncached events (not one-per-row).
 *  - Cache-first: events already cached are never re-requested; a session-level set de-dupes
 *    repeated primes (e.g. on every list re-render).
 *  - Every attempted event is written to the cache (incl. '' = none), so chips resolve without
 *    per-row fetches, and the cache self-prunes daily.
 */

export const DIAGNOSIS_UPDATE_EVENT = 'aumhum:appt-diagnosis-updated'

function isTherapistEvent(eventType) {
  return eventType === EVENT_TYPE_THERAPY_SESSION
}

// eventIds that have been resolved (cached) or are being fetched this session.
const settledOrInFlight = new Set()

function notifyUpdated() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(DIAGNOSIS_UPDATE_EVENT))
  }
}

/**
 * @param {Array<{eventId: string, eventType?: string, dayKey: string}>} candidates
 */
export async function primeDiagnosesForDay(candidates) {
  if (!Array.isArray(candidates) || candidates.length === 0) return
  try {
    pruneOldDiagnosisCacheOncePerDay()

    // Skip events already settled this session; cache-check the rest.
    const unseen = candidates.filter((c) => c?.eventId && !settledOrInFlight.has(c.eventId))
    if (unseen.length === 0) return

    const toFetch = []
    for (const c of unseen) {
      const cached = await getDiagnosisFromCache(c.eventId)
      if (cached) {
        settledOrInFlight.add(c.eventId)
      } else {
        toFetch.push(c)
      }
    }
    if (toFetch.length === 0) return

    const ids = toFetch.map((c) => c.eventId)
    ids.forEach((id) => settledOrInFlight.add(id))

    // Route uncached events by type, fetch each route in one batched call, then merge.
    const therapistIds = toFetch.filter((c) => isTherapistEvent(c.eventType)).map((c) => c.eventId)
    const doctorIds = toFetch.filter((c) => !isTherapistEvent(c.eventType)).map((c) => c.eventId)

    let labelById = new Map()
    try {
      const [doctorMap, therapistMap] = await Promise.all([
        doctorIds.length ? fetchDoctorLabelsForEvents(doctorIds) : new Map(),
        therapistIds.length ? fetchTherapistLabelsForEvents(therapistIds) : new Map(),
      ])
      labelById = new Map([...doctorMap, ...therapistMap])
    } catch (err) {
      // network failure → let these retry on a later prime
      ids.forEach((id) => settledOrInFlight.delete(id))
      return
    }

    // Write every attempted event ({kind,text} OR '' for "checked, none").
    await Promise.all(
      toFetch.map((c) => {
        const found = labelById.get(c.eventId)
        return putDiagnosisInCache({
          eventId: c.eventId,
          label: found?.text || '',
          kind: found?.kind || (isTherapistEvent(c.eventType) ? 'tx' : 'dx'),
          dayKey: c.dayKey,
        })
      }),
    )
    notifyUpdated()
  } catch (err) {
    /* best-effort */
  }
}
